import { VibrancyPanel } from '@lashpic/ui/admin';
import { Monitor } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { findNavItem } from '../lib/nav-config';
import { useIsMobile } from '../lib/use-is-mobile';

interface MobileGuardProps {
  children: React.ReactNode;
}

/**
 * 모바일에서 `mobileAllowed=false` 페이지에 접근하면 "데스크탑 사용" 모달을 띄운다.
 * docs/specs/superadmin.md §3.3 모바일 우선 기능 정의에 따른 게이트.
 */
export function MobileGuard({ children }: MobileGuardProps) {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const navItem = findNavItem(pathname);

  if (isMobile && navItem && !navItem.mobileAllowed) {
    return (
      <>
        {/* 뒤에 페이지를 흐릿하게 렌더 */}
        <div aria-hidden className="pointer-events-none select-none opacity-40 blur-sm">
          {children}
        </div>

        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-guard-title"
          className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
        >
          <VibrancyPanel variant="modal" className="w-full max-w-md p-6 sm:rounded-2xl">
            <div className="mb-3 grid size-12 place-items-center rounded-full bg-[var(--color-admin-blue)]/10 text-[var(--color-admin-blue)]">
              <Monitor className="size-6" aria-hidden />
            </div>
            <h2
              id="mobile-guard-title"
              className="font-serif text-lg font-semibold text-[var(--color-admin-text)]"
            >
              데스크탑에서 사용해주세요
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed text-[var(--color-admin-text-secondary)]">
              <strong>{navItem.label}</strong> 은(는) 정밀한 편집·프롬프트 작업이 필요해
              모바일에서는 읽기 전용으로도 제공하지 않습니다. 데스크탑 접속을 권장합니다.
            </p>
            <div className="mt-4 rounded-lg bg-black/[0.04] p-3 text-[12px] text-[var(--color-admin-text-secondary)] dark:bg-white/5">
              모바일에서는 <strong>Dashboard · Incidents · Rollout · Feedback</strong> 을 사용할 수
              있어요.
            </div>
            <a
              href="/overview/dashboard"
              className="admin-focus-ring mt-4 inline-flex w-full items-center justify-center rounded-lg bg-[var(--color-admin-blue)] px-4 py-2.5 text-[13px] font-semibold text-white"
            >
              Dashboard로 이동
            </a>
          </VibrancyPanel>
        </div>
      </>
    );
  }

  return <>{children}</>;
}
